import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { siteImages } from '../data/mock';
import { Target, Users, Award, Heart, PawPrint, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  useEffect(() => {
    document.title = 'About Us | Paw Supplies Group';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />

      <main>
        {/* Banner */}
        <section className="relative h-64 md:h-80 overflow-hidden">
          <img
            src={siteImages.aboutBanner}
            alt="About Us"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-brown/85 via-brand-brown/60 to-brand-brown/30" />
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-brand-cream mb-2">
                <PawPrint className="w-5 h-5" />
                <span className="text-xs font-bold uppercase tracking-[0.25em]">Our Story</span>
              </div>
              <h1 className="text-4xl md:text-5xl text-white font-extrabold mb-3" className-FONT-DISPLAY>
                About Paw Supplies Group
              </h1>
              <p className="text-brand-cream max-w-lg">
                Premium pet accessories curated for the pets we love
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Mission */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <span className="inline-block px-4 py-1.5 bg-brand-cream text-brand-orange-dark text-xs font-bold uppercase tracking-wider rounded-full mb-4 border border-brand-divider">
                Our Mission
              </span>
              <h2 className="text-3xl lg:text-4xl text-brand-brown font-extrabold mb-6" className-FONT-DISPLAY>
                Helping pet parents <span className="text-brand-orange">pamper their best friends</span>
              </h2>
              <p className="text-brand-brown-light text-lg leading-relaxed mb-6">
                At Paw Supplies Group, we believe pets are family. Our mission is simple: bring busy pet parents a thoughtfully curated lineup of accessories and equipment that make daily care easier and your pet&apos;s day a little brighter.
              </p>
              <p className="text-brand-brown-light leading-relaxed mb-8">
                From cozy beds and feeding bowls to engaging toys, walking gear and travel carriers, every item in our catalog is hand-picked with comfort, durability and pet-first design in mind.
              </p>
              <div className="flex flex-wrap gap-4">
                {['Pet-First Picks', 'Trusted Quality', 'Curated Selection'].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 px-4 py-2 bg-brand-cream rounded-xl border border-brand-divider">
                    <CheckCircle2 className="w-4 h-4 text-brand-orange" />
                    <span className="text-brand-brown text-sm font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-3xl overflow-hidden shadow-xl">
                <img
                  src={siteImages.heroLifestyle}
                  alt="Happy pets"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-brand-orange text-white px-6 py-4 rounded-2xl shadow-lg">
                <p className="font-extrabold text-2xl" className-FONT-DISPLAY>12+</p>
                <p className="text-sm font-semibold">Pet Products</p>
              </div>
            </div>
          </div>

          {/* Core Values */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-brand-cream text-brand-orange-dark text-xs font-bold uppercase tracking-wider rounded-full mb-4 border border-brand-divider">
                Core Values
              </span>
              <h2 className="text-3xl lg:text-4xl text-brand-brown font-extrabold" className-FONT-DISPLAY>
                What Drives Us
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Target, title: 'Pet First', desc: 'Every product is judged by how your pet experiences it, not just how it looks.' },
                { icon: Users, title: 'Pet Parents', desc: 'Built for the busy families and pet parents who want better for their fur kids.' },
                { icon: Award, title: 'Quality Picks', desc: 'We curate carefully so every order arrives at a standard we&apos;d use ourselves.' },
                { icon: Heart, title: 'Love-Led', desc: 'We love pets, and that love shows up in every recommendation we make.' },
              ].map((value, i) => (
                <div key={i} className="bg-brand-cream border border-brand-divider rounded-2xl p-6 hover:border-brand-orange/40 transition-all group">
                  <div className="w-14 h-14 bg-brand-orange-light/30 rounded-xl flex items-center justify-center mb-4 group-hover:bg-brand-orange-light/50 transition-colors">
                    <value.icon className="w-7 h-7 text-brand-orange-dark" />
                  </div>
                  <h3 className="font-bold text-brand-brown text-lg mb-2" className-FONT-DISPLAY>{value.title}</h3>
                  <p className="text-brand-brown-light text-sm leading-relaxed">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Why Choose Us */}
          <div className="bg-brand-cream border border-brand-divider rounded-3xl p-8 md:p-12">
            <div className="text-center mb-10">
              <h2 className="text-3xl lg:text-4xl text-brand-brown font-extrabold mb-4" className-FONT-DISPLAY>
                Why Choose Paw Supplies Group?
              </h2>
              <p className="text-brand-brown-light max-w-2xl mx-auto">
                We&apos;re committed to giving you and your pet a better shopping experience &mdash; every order, every time.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                'Premium pet accessories and equipment',
                'Fast, free USPS shipping across the U.S.A.',
                'Secure 256-bit SSL encrypted checkout',
                '30-day hassle-free returns',
                'Friendly customer support by email & phone',
                'A carefully curated, pet-first product lineup',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-4 bg-white rounded-xl">
                  <CheckCircle2 className="w-5 h-5 text-brand-orange flex-shrink-0" />
                  <span className="text-brand-brown font-medium">{item}</span>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 px-8 py-4 bg-brand-orange text-white font-bold uppercase tracking-wider rounded-full hover:bg-brand-orange transition-all shadow-lg"
              >
                Shop Our Pet Lineup
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
